import { isNull, isUndefined, noop } from 'lodash/fp';
import React, { useMemo } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { formatDate } from '../../../utils/luxon';
import { PreExecutionFilterOption } from '../../documents/search/store';
import { OverflowTooltip, TooltipList } from '../../shared/tooltip';
import { getAllClientDatasets } from '../client-datasets/store';
import { getAllDocumentNames } from '../documents/store';
import { getBaseEntityDropdownOptions } from '../entity/store';
import styles from './PortfolioAnalytics.module.scss';
import { PortfolioAnalysisTemplate } from './store';

interface PortfolioAnalyticsTemplateProps {
    template: PortfolioAnalysisTemplate;
    buttons?: JSX.Element;
    selectTemplate?: (template: PortfolioAnalysisTemplate) => void;
    selectOnSingleClick?: boolean;
}

export const PortfolioAnalyticsTemplate: React.FC<PortfolioAnalyticsTemplateProps> = ({ template, buttons = null, selectTemplate = noop, selectOnSingleClick = false }) => {
    const { portfolioAnalysisTemplateId, templateName, metaData, createdBy, createdDate, lastAskedBy, lastAskedDate } = template;
    const { parentDatasetId, question, fieldLabel, filters: { documentNameIds, entityIds, includePreExecution, date, isExclusiveEntityFilter } } = metaData;

    const datasets = useAppSelector(getAllClientDatasets);
    const allDocumentNames = useAppSelector(getAllDocumentNames);
    const entityOptions = useAppSelector(getBaseEntityDropdownOptions);

    const agreementType = useMemo(() => datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '', [datasets, parentDatasetId]);
    const documentNames = useMemo(() => allDocumentNames.filter(({ datasetId, documentNameId }) => datasetId === parentDatasetId && documentNameId && documentNameIds.includes(documentNameId)).map(({ documentName }) => documentName), [allDocumentNames, documentNameIds, parentDatasetId]);

    const entityFiltersApplied = useMemo(() => {
        if (isUndefined(entityIds)) {
            return null;
        }
        const getEntityName = (val: string) => entityOptions.find(({ value }) => value === val)?.label || '';
        return entityIds.map(value => getEntityName(value));
    }, [entityOptions, entityIds]);

    const preExecutionFilterApplied = useMemo(() => {
        if (isUndefined(includePreExecution) || includePreExecution === PreExecutionFilterOption.EXCLUDE) {
            return null;
        }
        if (includePreExecution === PreExecutionFilterOption.EXCLUSIVE) {
            return 'Exclusively Pre-Execution documents';
        }
        return 'Include Pre-Execution documents';
    }, [includePreExecution]);

    const dateFilterApplied = useMemo(() => !isUndefined(date) && !isNull(date.from) && !isNull(date.to), [date]);
    const hasFilters = useMemo(() => entityFiltersApplied || preExecutionFilterApplied || dateFilterApplied, [entityFiltersApplied, preExecutionFilterApplied, dateFilterApplied]);
    const fromDate = useMemo(() => dateFilterApplied ? formatDate(date!.from!) : '', [dateFilterApplied, date]);
    const toDate = useMemo(() => dateFilterApplied ? formatDate(date!.to!) : '', [dateFilterApplied, date]);
    return (
        <div className={styles.template} key={portfolioAnalysisTemplateId} onClick={() => selectTemplate(template)} style={{ cursor: selectOnSingleClick ? 'pointer' : 'default' }}>
            <OverflowTooltip className={styles.templateName} overlayText={templateName} />
            <div className={styles.templateBody}>
                <div className={styles.templateInfoWrapper}>
                    <div className={styles.surveyDetailsWrapper} style={{ maxWidth: hasFilters ? '60%' : '100%' }}>
                        <div className={styles.templateDetailWrapper}>
                            <div className={styles.templateDetailLabel}>Survey Information:</div>
                            <div className={styles.detailsWrapper}>
                                <div className={styles.templateDetail}>{agreementType}</div>
                                <TooltipList overlayText={documentNames} placement='bottom'>
                                    <div className={styles.templateDetail}>{documentNames.join(', ')}</div>
                                </TooltipList>
                                <div className={styles.templateDetail}>{fieldLabel}</div>
                            </div>
                        </div>
                        <div className={styles.templateDetailWrapper}>
                            <div className={styles.templateDetailLabel}>Question Asked:</div>
                            <div className={styles.detailsWrapper}>
                                <OverflowTooltip className={styles.templateDetail} overlayText={question} />
                            </div>
                        </div>
                        <div className={styles.templateDetailWrapper}>
                            <div className={styles.templateDetailLabel}>Created By:</div>
                            <div className={styles.detailsWrapper}>
                                <div className={styles.templateDetail}>{`${createdBy} on ${formatDate(createdDate)}`}</div>
                            </div>
                        </div>
                        <div className={styles.templateDetailWrapper}>
                            <div className={styles.templateDetailLabel}>Last Asked By:</div>
                            <div className={styles.detailsWrapper}>
                                <div className={styles.templateDetail}>{`${lastAskedBy} on ${formatDate(lastAskedDate)}`}</div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.filtersWrapper} style={{ width: hasFilters ? 'calc(40% - 10px)' : 'none' }}>
                        {hasFilters &&
                            <>
                                <div className={styles.filtersLabel}>Filters:</div>
                                <div className={styles.appliedFilterWrapper}>
                                    {entityFiltersApplied &&
                                        <TooltipList overlayText={entityFiltersApplied} placement='bottom'>
                                            <div className={styles.entities}>{entityFiltersApplied.join(', ')}</div>
                                        </TooltipList>
                                    }
                                    {isExclusiveEntityFilter &&
                                        <div className={styles.preExecution}>Include Exclusive Entities</div>
                                    }
                                    {preExecutionFilterApplied &&
                                        <div className={styles.preExecution}>{preExecutionFilterApplied}</div>
                                    }
                                    {dateFilterApplied &&
                                        <div className={styles.dateWrapper}>
                                            <div className={styles.dateRangeWrapper}>
                                                <div className={styles.dateRange}>
                                                    <div className={styles.dateRangeLabel}>Executed Date From:</div>
                                                    <div className={styles.dateRangeValue}>{fromDate}</div>
                                                </div>
                                                <div className={styles.dateRange}>
                                                    <div className={styles.dateRangeLabel}>To:</div>
                                                    <div className={styles.dateRangeValue}>{toDate}</div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </>
                        }
                    </div>
                </div>
                {buttons}
            </div>
        </div>
    );
};
